<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"

import { AxiosError } from "axios"

import QualityNameInput from "@/components/QualityNameInput.vue"
import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import { getLocaleMessages } from "@/locales/index"
import axios from "@/plugins/axios"
import router from "@/plugins/router"
import store from "@/plugins/store"
import type { Option } from "@/types/common.type"
import { ErrorCode, invalidParameterMessage } from "@/utils/error"
import { normalizeQualityName } from "@/utils/qualityName"
import { reportSentry } from "@/utils/sentry"

const { t } = useI18n()

const name = ref("")
const hsCode = ref("")
const countryOfOriginId = ref(0)
const isPrintOrderable = ref("false")

const minimumWidth = ref("")
const fullWidth = ref("")
const weight = ref("")
const yardWeight = ref("")

const categoryId = ref(0)
const patternId = ref(0)
const dyeMethodId = ref(0)
const contentIds = ref([])
const contentRatios = ref<[]>([])
const contents = computed(() => {
  return contentIds.value.map((id, idx) => ({ id: id, ratio: contentRatios.value[idx] }))
})
const yarnEffectIds = ref([])
const brandYarnIds = ref([])
const finishIds = ref([])
const appearanceIds = ref([])
const performanceIds = ref([])
const sustainabilityFactorIds = ref([])

const countryOptions = ref<Option[]>([])
const categoryOptions = ref<Option[]>([])
const patternOptions = ref<Option[]>([])
const dyeMethodOptions = ref<Option[]>([])
const contentOptions = ref<Option[]>([])
const yarnEffectOptions = ref<Option[]>([])
const brandYarnOptions = ref<Option[]>([])
const finishOptions = ref<Option[]>([])
const appearanceOptions = ref<Option[]>([])
const performanceOptions = ref<Option[]>([])
const sustainabilityFactorOptions = ref<Option[]>([])

async function loadCountries() {
  const countries = store.state.countries
  countryOptions.value = countries.map((e) => ({ label: e.name, value: e.id }))
}

async function loadProperties() {
  const data = store.state.properties

  if (data) {
    categoryOptions.value = data.categories.map((e) => ({ label: `${e.rootName} > ${e.name}`, value: e.id }))
    patternOptions.value = data.patterns.map((e) => ({ label: e.name, value: e.id }))
    dyeMethodOptions.value = data.dyeMethods.map((e) => ({ label: e.name, value: e.id }))
    contentOptions.value = data.contents.map((e) => ({ label: `${e.rootName} > ${e.name}`, value: e.id }))
    yarnEffectOptions.value = data.yarnEffects.map((e) => ({ label: e.name, value: e.id }))
    brandYarnOptions.value = data.brandYarns.map((e) => ({ label: e.name, value: e.id }))
    finishOptions.value = data.finishes.map((e) => ({ label: e.name, value: e.id }))
    appearanceOptions.value = data.appearances.map((e) => ({ label: e.name, value: e.id }))
    performanceOptions.value = data.performances.map((e) => ({ label: e.name, value: e.id }))
    sustainabilityFactorOptions.value = data.sustainabilityFactors.map((e) => ({ label: e.name, value: e.id }))
  }
}

onMounted(async () => {
  try {
    await loadCountries()
    await loadProperties()
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/items")
  }
})

const isSubmitting = ref(false)
const errorMessage = ref("")

async function saveItem() {
  if (isSubmitting.value) return
  name.value = normalizeQualityName(name.value)
  if (!name.value) {
    errorMessage.value = t("item.nameRequired")
    return
  }
  isSubmitting.value = true
  errorMessage.value = ""

  const url = "/qualities/basic_info"
  const params = {
    name: name.value,
    hsCode: hsCode.value,
    countryOfOriginId: countryOfOriginId.value,
    isPrintOrderable: isPrintOrderable.value,
    minimumWidth: minimumWidth.value,
    fullWidth: fullWidth.value,
    weight: weight.value,
    yardWeight: yardWeight.value,
    categoryId: categoryId.value,
    patternId: patternId.value,
    dyeMethodId: dyeMethodId.value,
    contents: contents.value,
    yarnEffectIds: yarnEffectIds.value,
    brandYarnIds: brandYarnIds.value,
    finishIds: finishIds.value,
    appearanceIds: appearanceIds.value,
    performanceIds: performanceIds.value,
    sustainabilityFactorIds: sustainabilityFactorIds.value,
  }

  try {
    let response = await axios.post(url, params)
    let data = response.data
    let itemId = data.id

    // TODO merge this part to POST at sever, and remove this block
    const url_detail = `/qualities/${itemId}/detail_info`
    const params_detail = {
      yarnEffectIds: yarnEffectIds.value,
      brandYarnIds: brandYarnIds.value,
      finishIds: finishIds.value,
      appearanceIds: appearanceIds.value,
      performanceIds: performanceIds.value,
      sustainabilityFactorIds: sustainabilityFactorIds.value,
    }
    response = await axios.put(url_detail, params_detail)
    data = response.data
    itemId = data.id
    // TODO

    router.push(`/items/${itemId}`)
  } catch (error) {
    console.log(error)
    reportSentry(error)

    if (error instanceof AxiosError && error.response) {
      const { code } = error.response.data
      if (code === ErrorCode.INVALID_PARAMETER) {
        errorMessage.value = invalidParameterMessage(error.response.data, {
          duplicateName: t("item.errors.duplicateName"),
          unknown: t("item.errors.invalidParameter"),
        })
      }
    }

    isSubmitting.value = false
  }
}
</script>

<template>
  <LayoutAuthed :title="$t('item.new')">
    <div class="row">
      <div class="col-12 col-lg-9 col-xxl-6">
        <form @submit.prevent="saveItem()">
          <Card>
            <CardTitle>{{ $t("item.basic") }}</CardTitle>
            <CardContent>
              <QualityNameInput v-model="name" />
              <InputBox v-model="hsCode" type="text" field="hsCode" :label="$t('item.hsCode')" />
              <InputSelect
                v-model="countryOfOriginId"
                field="countryOfOriginId"
                :label="$t('item.countryOfOrigin')"
                :options="countryOptions"
                searchable
                required
              />
              <InputSelect
                v-model="isPrintOrderable"
                field="isPrintOrderable"
                :label="$t('item.isPrintOrderable')"
                :options="Object.entries(getLocaleMessages().shared.boolean).map(([v, k]) => ({ label: k, value: v }))"
                required
              />
            </CardContent>
            <CardTitle>{{ $t("item.dimensions") }}</CardTitle>
            <CardContent>
              <InputBox
                v-model.number="minimumWidth"
                type="number"
                field="minimumWidth"
                :label="`${$t('item.minimumWidth')} (${$t('item.units.minimumWidth')})`"
                :min="30"
                :max="70"
                required
              />
              <InputBox
                v-model.number="fullWidth"
                type="number"
                field="fullWidth"
                :label="`${$t('item.fullWidth')} (${$t('item.units.fullWidth')})`"
                :min="30"
                :max="70"
              />
              <InputBox
                v-model.number="weight"
                type="number"
                field="weight"
                :label="`${$t('item.weight')} (${$t('item.units.weight')})`"
                :min="30"
                :max="1000"
                required
              />
              <InputBox
                v-model.number="yardWeight"
                type="number"
                field="yardWeight"
                :label="`${$t('item.yardWeight')} (${$t('item.units.yardWeight')})`"
                :min="30"
                :max="1000"
              />
            </CardContent>
            <CardTitle>{{ $t("item.tags") }}</CardTitle>
            <CardContent>
              <InputSelect
                v-model="categoryId"
                field="categoryId"
                :label="$t('item.category')"
                :options="categoryOptions"
                searchable
                required
              />
              <InputSelect
                v-model="patternId"
                field="patternId"
                :label="$t('item.pattern')"
                :options="patternOptions"
                searchable
                required
              />
              <InputSelect
                v-model="dyeMethodId"
                field="dyeMethodId"
                :label="$t('item.dyeMethod')"
                :options="dyeMethodOptions"
                searchable
                required
              />
              <InputSelect
                v-model="contentIds"
                field="contentIds[]"
                :label="$t('item.contents')"
                :options="contentOptions"
                searchable
                multiple
                required
              />
              <InfoTable class="my-2 ms-3">
                <InfoRow
                  v-for="(contentId, idx) in contentIds"
                  :name="contentOptions.find((e) => e.value === contentId)?.label || ''"
                >
                  <InputBox v-model="contentRatios[idx]!" type="number" field="contentRatios[]" prefix="%" />
                </InfoRow>
              </InfoTable>
              <InputSelect
                v-model="yarnEffectIds"
                field="yarnEffectIds[]"
                :label="$t('item.yarnEffects')"
                :options="yarnEffectOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="brandYarnIds"
                field="brandYarnIds[]"
                :label="$t('item.brandYarns')"
                :options="brandYarnOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="finishIds"
                field="finishIds[]"
                :label="$t('item.finishes')"
                :options="finishOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="appearanceIds"
                field="appearanceIds[]"
                :label="$t('item.appearances')"
                :options="appearanceOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="performanceIds"
                field="performanceIds[]"
                :label="$t('item.performances')"
                :options="performanceOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="sustainabilityFactorIds"
                field="sustainabilityFactorIds[]"
                :label="$t('item.sustainabilityFactors')"
                :options="sustainabilityFactorOptions"
                searchable
                multiple
              />
            </CardContent>
            <CardActions displayBlock>
              <Button type="submit" color="dark" :text="$t('shared.save')" :isSubmitting="isSubmitting" />
              <span class="text-center text-danger error-message">{{ errorMessage }}</span>
            </CardActions>
          </Card>
        </form>
      </div>
    </div>
  </LayoutAuthed>
</template>

<style scoped>
.error-message {
  font-size: 16px;
  font-weight: bold;
}
</style>
